<template>
  <div class="relative">
    <div
      class="pointer-events-none absolute inset-0 z-40"
      :style="{ boxShadow: combinedShadows }"
    />

    <div
      ref="scrollContainer"
      :class="[
        'overflow-auto relative hide-scrollbar',
        wrapperClass ?? 'w-full h-full',
        {
          'cursor-grabbing': isPanning && shouldShowScrollPreview,
          'cursor-grab': isAltPressed && !isPanning,
        },
      ]"
      @scroll="handleScroll"
      @mousedown="handleMouseDown"
      @mouseup="handleMouseUp"
      @mouseleave="handleMouseUp"
      @mousemove="handleMouseMove"
    >
      <div
        v-if="isPanning && shouldShowScrollPreview"
        class="mousehint"
        :style="{
          top: `${mouseY + 12}px`,
          left: `${mouseX + 12}px`,
        }"
      >
        {{ t(`titletags.pan`) }}
      </div>

      <slot />
    </div>
    <ScrollDragPreview
      v-if="enableScrollPreview && scrollMetrics && shouldShowScrollPreview"
      :visible="isPreviewVisible"
      :mapWidth="200"
      :scrollPosition="scrollMetrics.scrollPosition"
      :scrollAreaSize="scrollMetrics.scrollAreaSize"
      :viewportSize="scrollMetrics.viewportSize"
      :onDragScroll="handleDragScroll"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useScrollPositionStore } from '@/stores/scrollPosition';

import ScrollDragPreview from './ScrollDragPreview.vue';

const props = defineProps<{
  storageKey?: string;
  enablePanning?: boolean;
  wrapperClass?: string;
  enableScrollPreview?: boolean;
}>();

const { t } = useI18n();
const key = props.storageKey ?? window.location.pathname;
const isAltPressed = ref(false);
const scrollStore = useScrollPositionStore();

const scrollContainer = ref<HTMLElement | null>(null);
const isPanning = ref(false);
const startX = ref(0);
const startY = ref(0);
const scrollLeft = ref(0);
const scrollTop = ref(0);
const mouseX = ref(0);
const mouseY = ref(0);

const hasShadowTop = ref(false);
const hasShadowBottom = ref(false);
const hasShadowLeft = ref(false);
const hasShadowRight = ref(false);

const scrollPosition = reactive({ left: 0, top: 0 });

const isPreviewVisible = ref(false);
let hidePreviewTimeout: ReturnType<typeof setTimeout> | null = null;

const shouldShowScrollPreview = computed(() => {
  const m = scrollMetrics.value;
  if (!m) return false;

  const isScrollableX = m.scrollAreaSize.width > m.viewportSize.width;
  const isScrollableY = m.scrollAreaSize.height > m.viewportSize.height;

  return isScrollableX || isScrollableY;
});

function showPreview() {
  isPreviewVisible.value = true;
  if (hidePreviewTimeout) clearTimeout(hidePreviewTimeout);
  hidePreviewTimeout = setTimeout(() => {
    isPreviewVisible.value = false;
  }, 3000); // z. B. 3 Sekunden sichtbar
}

function updateScrollPosition() {
  const el = scrollContainer.value;
  if (!el) return;
  scrollPosition.left = el.scrollLeft;
  scrollPosition.top = el.scrollTop;
}

const scrollMetrics = computed(() => {
  const el = scrollContainer.value;
  if (!el) return null;

  return {
    scrollPosition: {
      left: scrollPosition.left,
      top: scrollPosition.top,
    },
    scrollAreaSize: {
      width: el.scrollWidth,
      height: el.scrollHeight,
    },
    viewportSize: {
      width: el.clientWidth,
      height: el.clientHeight,
    },
  };
});

function handleContextMenu(e: MouseEvent) {
  if (isPanning.value) {
    e.preventDefault();
  }
}

function handleScroll() {
  if (!scrollContainer.value) return;
  const el = scrollContainer.value;

  const sl = el.scrollLeft;
  const st = el.scrollTop;
  const sw = el.scrollWidth;
  const sh = el.scrollHeight;
  const cw = el.clientWidth;
  const ch = el.clientHeight;

  const threshold = 10;

  hasShadowLeft.value = sl > threshold;
  hasShadowRight.value = sw - cw - sl > threshold;

  hasShadowTop.value = st > threshold;
  hasShadowBottom.value = sh - ch - st > threshold;

  updateScrollPosition();
  scrollStore.setPosition(key, {
    scrollLeft: scrollContainer.value.scrollLeft,
    scrollTop: scrollContainer.value.scrollTop,
  });
}

function restoreScroll() {
  const container = scrollContainer.value;
  if (!container) return;

  const pos = scrollStore.restorePosition(key);
  requestAnimationFrame(() => {
    // No saved position? Try centering horizontally if necessary
    if (!pos) {
      const scrollWidth = container.scrollWidth;
      const clientWidth = container.clientWidth;
      if (scrollWidth > clientWidth) {
        container.scrollLeft = (scrollWidth - clientWidth) / 2;
      }
      return;
    }

    // Only scroll if content is large enough
    if (pos.scrollLeft > 0 && container.scrollWidth > container.clientWidth) {
      container.scrollLeft = pos.scrollLeft;
    }

    if (pos.scrollTop > 0 && container.scrollHeight > container.clientHeight) {
      container.scrollTop = pos.scrollTop;
    }
  });
}

function handleMouseDown(e: MouseEvent) {
  const isAlt = e.altKey;
  const isRightClick = e.button === 2;

  if (props.enablePanning && (isAlt || isRightClick)) {
    showPreview();
    isPanning.value = true;
    startX.value = e.clientX;
    startY.value = e.clientY;
    if (scrollContainer.value) {
      scrollLeft.value = scrollContainer.value.scrollLeft;
      scrollTop.value = scrollContainer.value.scrollTop;
    }

    updateScrollPosition();
    if (isRightClick) e.preventDefault();
  }
}

function handleMouseUp() {
  isPanning.value = false;
}

function handleMouseMove(e: MouseEvent) {
  mouseX.value = e.clientX;
  mouseY.value = e.clientY;

  if (!isPanning.value || !scrollContainer.value) return;
  const dx = e.clientX - startX.value;
  const dy = e.clientY - startY.value;
  scrollContainer.value.scrollLeft = scrollLeft.value - dx;
  scrollContainer.value.scrollTop = scrollTop.value - dy;
}

function handleDragScroll(pos: { left: number; top: number }) {
  if (scrollContainer.value) {
    scrollContainer.value.scrollLeft = pos.left;
    scrollContainer.value.scrollTop = pos.top;
    scrollPosition.left = pos.left;
    scrollPosition.top = pos.top;

    // Vorschau beim Drag nicht automatisch verstecken:
    if (hidePreviewTimeout) {
      clearTimeout(hidePreviewTimeout);
      hidePreviewTimeout = null;
    }

    // Vorschau z. B. nach Drag-Ende neu verstecken:
    hidePreviewTimeout = setTimeout(() => {
      isPreviewVisible.value = false;
    }, 3000);
  }
}

function handleKeyDown(e: KeyboardEvent) {
  if (e.altKey) isAltPressed.value = true;
}

function handleKeyUp(e: KeyboardEvent) {
  if (!e.altKey) isAltPressed.value = false;
}

onMounted(() => {
  restoreScroll();
  window.addEventListener('contextmenu', handleContextMenu);
  window.addEventListener('keydown', handleKeyDown);
  window.addEventListener('keyup', handleKeyUp);
});

onBeforeUnmount(() => {
  handleScroll();
  window.removeEventListener('contextmenu', handleContextMenu);
  window.removeEventListener('keydown', handleKeyDown);
  window.removeEventListener('keyup', handleKeyUp);
});

const combinedShadows = computed(() => {
  const shadows: string[] = [];
  if (hasShadowTop.value) shadows.push('inset 0 12px 12px -12px rgba(0, 0, 0, 0.3)');
  if (hasShadowBottom.value) shadows.push('inset 0 -12px 12px -12px rgba(0, 0, 0, 0.3)');
  if (hasShadowLeft.value) shadows.push('inset 12px 0 12px -12px rgba(0, 0, 0, 0.3)');
  if (hasShadowRight.value) shadows.push('inset -12px 0 12px -12px rgba(0, 0, 0, 0.3)');
  return shadows.join(', ');
});
</script>

<style scoped lang="css">
@config "../../../tailwind.config.js";
@reference "tailwindcss";
@reference "tailwindcss/utilities";
@layer app {
  .mousehint {
    @apply fixed z-50 bg-gray-700 text-white text-sm px-3 py-1 rounded shadow pointer-events-none;
  }

  div {
    user-select: none;
  }

  .cursor-grab {
    cursor: grab;
  }
  .cursor-grabbing {
    cursor: grabbing;
  }

  .has-shadow-top {
    box-shadow: inset 0 6px 6px -6px rgba(0, 0, 0, 0.15);
  }

  .has-shadow-bottom {
    box-shadow: inset 0 -6px 6px -6px rgba(0, 0, 0, 0.15);
  }

  .has-shadow-left {
    box-shadow: inset 6px 0 6px -6px rgba(0, 0, 0, 0.15);
  }

  .has-shadow-right {
    box-shadow: inset -6px 0 6px -6px rgba(0, 0, 0, 0.15);
  }

  .hide-scrollbar {
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE 10+ */
  }

  .hide-scrollbar::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
  }
}
</style>
